import {
  checkProgressInterval,
  maxProgressInterval,
  progressJitter,
  batchPollMax
} from '../constants'

// Shared machinery for batched pollers: one timer for all registered rows,
// immediate-coalescing on register, 429 backoff (honoring Retry-After), and
// request chunking. Subclasses supply what to poll and how to handle a cycle:
//
//   _collectIds()          -> ids to fetch this cycle
//   _fetch(chunk)          -> Promise<response> for one chunk of ids
//   _handleData(data, ids) -> process a successful batch (data keyed by id)
//   _handleError(error, ids) -> react to a failed batch (after backoff is applied)
//   _hasWork()             -> whether anything is still registered
//
// Requests are chunked to batchPollMax ids; a subclass can override this.batchMax
// if its endpoint has a different server cap.
class BatchPollingManager {
  constructor() {
    this.timer = null
    this.inFlight = false
    this.immediateScheduled = false
    this.currentDelay = checkProgressInterval
    this.batchMax = batchPollMax
  }

  _collectIds() {
    return []
  }

  _fetch() {
    return Promise.resolve({ data: {} })
  }

  _handleData() {}

  _handleError() {}

  _hasWork() {
    return false
  }

  _ensureTimer() {
    if (!this.timer) {
      this._scheduleNext()
    }
  }

  _stopTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  scheduleImmediate() {
    if (this.immediateScheduled) {
      return
    }

    this.immediateScheduled = true

    // setTimeout(0) coalesces all sync register/requestImmediatePoll calls into one _tick.
    setTimeout(() => {
      this.immediateScheduled = false
      this._tick()
    }, 0)
  }

  _scheduleNext() {
    if (this.timer) {
      clearTimeout(this.timer)
    }

    const jitter = Math.random() * progressJitter
    this.timer = setTimeout(() => this._tick(), this.currentDelay + jitter)
  }

  // Reschedule only while work remains; otherwise stop so the timer never spins
  // idly forever (e.g. after a register -> scheduleImmediate -> unregister race).
  _rescheduleOrStop() {
    if (this._hasWork()) {
      this._scheduleNext()
    } else {
      this._stopTimer()
    }
  }

  _chunk(items, size) {
    const chunks = []
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size))
    }

    return chunks
  }

  async _tick() {
    if (this.inFlight) {
      this._scheduleNext()
      return
    }

    const ids = this._collectIds()
    if (ids.length === 0) {
      this._rescheduleOrStop()
      return
    }

    this.inFlight = true
    try {
      // Chunk so the server cap never drops ids; usually a single request.
      const chunks = this._chunk(ids, this.batchMax)
      const responses = await Promise.all(chunks.map(chunk => this._fetch(chunk)))
      const data = Object.assign({}, ...responses.map(response => response.data))

      // Reset backoff and hand the batch to the subclass.
      this.currentDelay = checkProgressInterval
      await this._handleData(data, ids)
    } catch (error) {
      this._applyBackoff(error)
      this._handleError(error, ids)
    } finally {
      this.inFlight = false
      this._rescheduleOrStop()
    }
  }

  _applyBackoff(error) {
    const status = error && error.response && error.response.status
    // Only 429 backs off; other errors keep the base cadence.
    if (status !== 429) {
      return
    }

    // express-rate-limit sends Retry-After in seconds; fall back to doubling.
    let nextDelay = this.currentDelay * 2
    const retryAfter = error.response.headers && parseInt(error.response.headers['retry-after'], 10)
    if (retryAfter && !Number.isNaN(retryAfter)) {
      nextDelay = retryAfter * 1000
    }

    this.currentDelay = Math.min(nextDelay, maxProgressInterval)
  }
}

export default BatchPollingManager
